import { APP_VERSION } from '@/config/env';

export const RUNNING_VERSION = APP_VERSION;

const PUBLIC_BASE = (process.env.PUBLIC_URL || '/lms').replace(/\/+$/, '');
const RELOAD_ATTEMPT_KEY = 'lms:app-version-reload-attempt';

export function getVersionManifestUrl() {
  return `${PUBLIC_BASE}/version.json`;
}

export async function fetchLatestVersion() {
  if (!RUNNING_VERSION) {
    return { ok: false, latestVersion: null };
  }

  const url = `${getVersionManifestUrl()}?t=${Date.now()}`;
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
      return { ok: false, latestVersion: null };
    }
    const data = await res.json();
    const latestVersion = String(data?.version || '').trim();
    if (!latestVersion) {
      return { ok: false, latestVersion: null };
    }
    return {
      ok: true,
      latestVersion,
      updateAvailable: latestVersion !== RUNNING_VERSION,
    };
  } catch {
    return { ok: false, latestVersion: null };
  }
}

/** Avoid infinite reload when the browser keeps serving a stale bundle after refresh. */
export function isReloadBlockedForVersion(targetVersion) {
  if (!targetVersion) return false;
  try {
    return sessionStorage.getItem(RELOAD_ATTEMPT_KEY) === targetVersion;
  } catch {
    return false;
  }
}

export function markReloadAttempt(targetVersion) {
  if (!targetVersion) return;
  try {
    sessionStorage.setItem(RELOAD_ATTEMPT_KEY, targetVersion);
  } catch {
    /* ignore */
  }
}

export function clearReloadAttempt() {
  try {
    sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
  } catch {
    /* ignore */
  }
}

/** Best-effort cache clear before loading the new deployment. */
export async function clearAppCaches() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    /* ignore */
  }

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }
  } catch {
    /* ignore */
  }
}

/** Full page reload with cache-bust query so users get the newest bundle. */
export async function hardReloadForUpdate(targetVersion) {
  if (targetVersion) {
    markReloadAttempt(targetVersion);
  }
  await clearAppCaches();
  const url = new URL(window.location.href);
  url.searchParams.set('_appRefresh', String(Date.now()));
  window.location.replace(url.toString());
}

/**
 * When a new deployment is live, reload immediately (unless we already tried once).
 * Returns true if a reload was triggered.
 */
export async function reloadIfUpdateAvailable(latestVersion) {
  if (!latestVersion || latestVersion === RUNNING_VERSION) {
    clearReloadAttempt();
    return false;
  }
  if (isReloadBlockedForVersion(latestVersion)) {
    return false;
  }
  await hardReloadForUpdate(latestVersion);
  return true;
}
